import { useState, useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Button,
  Input,
  Label,
  useAuth,
} from '@aaas/shared';
import employeeTypeService from '../services/employee-type.service';
import type {
  EmployeeType,
  EmployeeTypeCreateInput,
  EmployeeTypeUpdateInput,
} from '../types/employee-type';

interface EmployeeTypeFormDialogProps {
  open: boolean;
  onClose: () => void;
  mode: 'create' | 'edit';
  employeeType?: EmployeeType | null;
  onSuccess: () => void;
}

export function EmployeeTypeFormDialog({
  open,
  onClose,
  mode,
  employeeType,
  onSuccess,
}: EmployeeTypeFormDialogProps) {
  const { user, currentWorkspace } = useAuth();
  const { t } = useTranslation('ats');
  const [name, setName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset form when dialog opens or employeeType changes
  useEffect(() => {
    if (open) {
      if (mode === 'edit' && employeeType) {
        setName(employeeType.name);
      } else {
        setName('');
      }
      setError(null);
    }
  }, [open, mode, employeeType]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!user || !currentWorkspace) return;
    if (!name.trim()) {
      setError(t('employeeType.nameLabel'));
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const token = await user.getIdToken();

      if (mode === 'create') {
        const data: EmployeeTypeCreateInput = {
          name: name.trim(),
        };
        await employeeTypeService.createEmployeeType(currentWorkspace.id, token, data);
      } else if (employeeType) {
        const data: EmployeeTypeUpdateInput = {
          name: name.trim(),
        };
        await employeeTypeService.updateEmployeeType(currentWorkspace.id, employeeType.id, token, data);
      }

      onSuccess();
      onClose();
    } catch (err: unknown) {
      console.error(`Error ${mode === 'create' ? 'creating' : 'updating'} employee type:`, err);
      const errorMessage = err instanceof Error ? err.message : t('config.unknownError');
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>
            {mode === 'create' ? t('employeeType.createTitle') : t('employeeType.editTitle')}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">{t('employeeType.nameLabel')}</Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('employeeType.namePlaceholder')}
              disabled={isSubmitting}
              autoFocus
            />
          </div>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose} disabled={isSubmitting}>
              {t('config.cancel')}
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {t('config.creating')}
                </>
              ) : mode === 'create' ? (
                t('employeeType.createButton')
              ) : (
                t('employeeType.updateButton')
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
